import React, { useEffect, useRef, useState } from 'react';
import { FileText, UploadCloud, Camera, X, Eye, Loader2, CheckCircle2 } from 'lucide-react';
import { stageDoc, pickPassportPage, removeStaged, fetchPreviewObjectUrl, portalErrorMessage } from '../api/portal';

/**
 * One typed document slot, repeatable up to `max` files (2026-07-24 — was a single file per slot
 * before). Uploads → server normalises to PDF → each accepted file is one entry in the parent's
 * `items` array ({ stageId, pageCount, thumbnails }). For the passport (singlePage), if an upload
 * has >1 page we show a thumbnail picker so the candidate taps the bio page (server keeps exactly
 * that page) before it's added to `items` — passport's max is always 1, so in practice this picker
 * only ever gates the slot's single item.
 *
 * Props: type, label, hint, required, singlePage, items ([{stageId,pageCount,thumbnails}]), max,
 *        onAdd(type, value), onRemoveAt(type, index), disabled.
 */
export default function DocSlot({ type, label, hint, required, singlePage, items, max, onAdd, onRemoveAt, disabled }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // A staged-but-not-yet-added upload awaiting a passport-page pick. Only ever set when singlePage
  // and the upload actually has usable multi-page thumbnails to choose from — otherwise (or once
  // resolved) the file goes straight into `items` via onAdd.
  const [pending, setPending] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const picking = !!pending;

  async function onPick(file) {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { setError(`“${file.name}” is larger than 15 MB.`); return; }
    setBusy(true); setError('');
    try {
      const res = await stageDoc(file, type);
      const thumbnails = res.thumbnails || [];
      if (singlePage && res.pageCount > 1 && thumbnails.length > 1) {
        setPending({ stageId: res.stageId, pageCount: res.pageCount, thumbnails });
      } else {
        onAdd(type, { stageId: res.stageId, pageCount: res.pageCount, thumbnails });
      }
    } catch (err) {
      setError(portalErrorMessage(err, 'Could not upload that file.'));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function choosePassportPage(pageIndex) {
    setBusy(true); setError('');
    try {
      const res = await pickPassportPage(pending.stageId, pageIndex);
      onAdd(type, { stageId: pending.stageId, pageCount: res.pageCount, thumbnails: [pending.thumbnails[pageIndex]] });
      setPending(null);
    } catch (err) {
      setError(portalErrorMessage(err, 'Could not set the passport page.'));
    } finally { setBusy(false); }
  }

  function removeAt(index) {
    const item = items[index];
    if (item?.stageId) removeStaged(item.stageId);
    onRemoveAt(type, index);
  }

  async function openPreview(stageId) {
    try { setPreviewUrl(await fetchPreviewObjectUrl(stageId)); }
    catch { setError('Could not open the preview.'); }
  }
  function closePreview() {
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  }
  // Revoke the object URL if the component unmounts (or the URL changes) while a preview is open.
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const canAddMore = items.length < max;

  return (
    <div className={`docslot ${items.length > 0 ? 'docslot-filled' : ''}`}>
      <div className="docslot-head">
        <span className="docslot-label">
          <FileText size={16} /> {label}
          {required ? <span className="docslot-req">required</span> : <span className="docslot-opt">optional</span>}
        </span>
        {items.length > 0 && !picking && <CheckCircle2 size={16} className="docslot-check" />}
      </div>
      {hint && items.length === 0 && <p className="docslot-hint">{hint}</p>}

      <input
        ref={inputRef} type="file" hidden aria-label={`Upload ${label}`}
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
        onChange={(e) => onPick(e.target.files?.[0] || null)}
      />

      {items.map((item, i) => (
        <div className="docslot-file" key={item.stageId || i}>
          {item.thumbnails?.[0] ? <img className="docslot-mini" src={item.thumbnails[0]} alt="" /> : <FileText size={18} />}
          <span className="docslot-file-meta">{item.pageCount} page{item.pageCount === 1 ? '' : 's'} · PDF</span>
          <button type="button" className="docslot-icon" onClick={() => openPreview(item.stageId)} title="Preview"
            aria-label={items.length > 1 ? `Preview ${label} ${i + 1}` : `Preview ${label}`}><Eye size={16} /></button>
          <button type="button" className="docslot-icon" onClick={() => removeAt(i)} title="Remove" disabled={busy}
            aria-label={items.length > 1 ? `Remove ${label} ${i + 1}` : `Remove ${label}`}><X size={16} /></button>
        </div>
      ))}

      {/* Passport page picker — a staged upload awaiting a single-page choice, not yet in `items` */}
      {picking && (
        <div className="docslot-pick">
          <p className="docslot-hint">Tap the passport page (the one with the photo):</p>
          <div className="docslot-thumbs">
            {pending.thumbnails.map((t, i) => (
              <button type="button" key={i} className="docslot-thumb" onClick={() => choosePassportPage(i)} disabled={busy}>
                <img src={t} alt={`Page ${i + 1}`} />
                <span>Page {i + 1}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {!picking && canAddMore && (
        <button type="button" className="docslot-add" onClick={() => inputRef.current?.click()} disabled={disabled || busy}>
          {busy ? <Loader2 size={18} className="spin" /> : singlePage ? <Camera size={18} /> : <UploadCloud size={18} />}
          <span>{busy ? 'Uploading…' : items.length > 0 ? `Add another ${label}` : singlePage ? 'Take a photo or choose a file' : `Add ${label}`}</span>
        </button>
      )}

      {error && <p className="docslot-err">{error}</p>}

      {previewUrl && (
        <div className="docslot-preview-overlay" role="dialog" aria-modal="true" aria-label={`${label} preview`} onClick={closePreview}>
          <div className="docslot-preview" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="docslot-preview-close" onClick={closePreview} aria-label="Close preview"><X size={18} /></button>
            <iframe title={`${label} preview`} src={previewUrl} />
          </div>
        </div>
      )}
    </div>
  );
}
